import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { MinoLogo } from './chamaTheme';

const FONT_SANS = "'Geist', 'SF Pro Display', -apple-system, sans-serif";
const FONT_MONO = "'Geist Mono', 'SF Mono', monospace";
const API = '/api/chamas';

export default function ChamaOnboarding({ onJoined }) {
  const { T } = useTheme();
  const [tab, setTab] = useState('create');
  const [form, setForm] = useState({ name: '', description: '', vote_before_trade: false });
  const [joinToken, setJoinToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');

  const handleCreate = async () => {
    if (!form.name.trim()) return setError('Chama name is required');
    setLoading(true); setError('');
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onJoined(data.chama);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleJoin = async () => {
    if (!joinToken.trim()) return setError('Invite token is required');
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/join/${joinToken.trim()}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onJoined({ id: data.chama_id });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 10,
    padding: '12px 14px', color: T.text, fontFamily: FONT_SANS, fontSize: 14, outline: 'none',
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: FONT_SANS, color: T.text, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}`}</style>

      <div style={{ width: '100%', maxWidth: 460, animation: 'fadeIn 0.4s ease' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 10, display: 'inline-flex' }}>
              <MinoLogo size={34} />
            </div>
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: T.text, margin: '0 0 6px', letterSpacing: '-0.02em' }}>Chama Mode</h1>
          <p style={{ color: T.textMuted, fontSize: 15, margin: 0 }}>Trade together. Win together.</p>
        </div>

        {/* Card */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>

          {/* Tabs */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, background: T.border }}>
            {['create', 'join'].map(t => (
              <button key={t} onClick={() => { setTab(t); setError(''); }} style={{
                padding: '14px', background: tab === t ? T.surface : T.surfaceHi,
                border: 'none', cursor: 'pointer', fontFamily: FONT_SANS, fontSize: 13,
                fontWeight: tab === t ? 600 : 400, color: tab === t ? T.text : T.textMuted,
                borderBottom: tab === t ? `2px solid ${T.accent}` : '2px solid transparent',
                transition: 'all 0.15s',
              }}>{t === 'create' ? 'Create Chama' : 'Join Chama'}</button>
            ))}
          </div>

          {/* Form */}
          <div style={{ padding: '28px' }}>
            {tab === 'create' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', color: T.textDim, fontSize: 10, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>Chama Name</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Westlands Trading Crew" maxLength={50} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', color: T.textDim, fontSize: 10, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>Description</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="What's your chama about? (optional)" rows={3}
                    style={{ ...inputStyle, resize: 'none', lineHeight: 1.6 }} />
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 16px',
                }}>
                  <div>
                    <div style={{ color: T.text, fontSize: 14, fontWeight: 500 }}>Vote before trade</div>
                    <div style={{ color: T.textDim, fontSize: 12, marginTop: 2 }}>Members must vote to approve trades</div>
                  </div>
                  <div onClick={() => setForm(f => ({ ...f, vote_before_trade: !f.vote_before_trade }))}
                    style={{
                      width: 42, height: 22, borderRadius: 11, cursor: 'pointer', position: 'relative', flexShrink: 0,
                      background: form.vote_before_trade ? T.accent : T.border,
                      transition: 'background 0.2s',
                    }}>
                    <div style={{
                      position: 'absolute', top: 3, left: form.vote_before_trade ? 22 : 3,
                      width: 16, height: 16, borderRadius: '50%', background: '#fff',
                      transition: 'left 0.2s cubic-bezier(0.34,1.56,0.64,1)',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </div>
                </div>
                {error && <div style={{ background: T.noDim, border: `1px solid ${T.no}30`, borderRadius: 8, padding: '10px 14px', color: T.no, fontSize: 13 }}>{error}</div>}
                <button onClick={handleCreate} disabled={loading} style={{
                  width: '100%', padding: '13px', borderRadius: 10, border: 'none',
                  background: T.accent, color: '#fff', fontFamily: FONT_SANS,
                  fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1, marginTop: 4,
                }}>{loading ? 'Creating...' : 'Create Chama'}</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <p style={{ color: T.textMuted, fontSize: 13, margin: 0, lineHeight: 1.6 }}>
                  Paste the invite token or full invite URL you received from your chama admin.
                </p>
                <div>
                  <label style={{ display: 'block', color: T.textDim, fontSize: 10, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>Invite Token</label>
                  <input value={joinToken} onChange={e => {
                    const match = e.target.value.match(/join\/([a-f0-9]+)/);
                    setJoinToken(match ? match[1] : e.target.value);
                  }} placeholder="Paste token or invite URL" style={inputStyle} />
                </div>
                {error && <div style={{ background: T.noDim, border: `1px solid ${T.no}30`, borderRadius: 8, padding: '10px 14px', color: T.no, fontSize: 13 }}>{error}</div>}
                <button onClick={handleJoin} disabled={loading} style={{
                  width: '100%', padding: '13px', borderRadius: 10, border: 'none',
                  background: T.accent, color: '#fff', fontFamily: FONT_SANS,
                  fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}>{loading ? 'Joining...' : 'Join Chama'}</button>
              </div>
            )}
          </div>
        </div>

        <p style={{ textAlign: 'center', color: T.textDim, fontSize: 12, marginTop: 20, lineHeight: 1.6 }}>
          Chamas are groups of up to 10 traders.<br />KES 500 minimum pool to activate trading.
        </p>
      </div>
    </div>
  );
}